import React from "react";
import { Link } from "react-router-dom";
import { SERVICES, VALUES, STATS, PROJECTS, HERO_IMG, REBAR_IMG } from "@/lib/content";

export default function Home() {
  return (
    <div className="bg-[#0a0a0a] text-white" data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[100vh] flex items-end overflow-hidden noise pt-20">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Chantier béton DCR-BATI" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40" />
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 pb-20 md:pb-28 pt-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8 fade-up">
              <div className="flex items-center gap-3 mb-6">
                <div className="hazard-stripes w-12 h-3" />
                <div className="eyebrow">Île-de-France · Gros œuvre · Béton armé</div>
              </div>
              <h1 className="font-display font-bold uppercase tracking-tighter text-white text-5xl md:text-7xl lg:text-8xl leading-[0.92]">
                Bâtir <span className="text-[#ffd600]">solide.</span>
                <br />
                Bâtir <span className="text-[#ffd600]">précis.</span>
                <br />
                Bâtir <span className="text-[#ffd600]">durable.</span>
              </h1>
              <p className="mt-8 text-neutral-300 text-lg max-w-2xl leading-relaxed">
                DCR-BATI réalise vos ouvrages neufs en béton armé : coffrage, planchers, platelage de poutres et structures porteuses. Une exécution rigoureuse, conforme aux normes, livrée dans les délais.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/devis" className="btn-primary" data-testid="hero-devis-cta">
                  Demander un devis <i className="fa-solid fa-arrow-right" />
                </Link>
                <Link to="/services" className="btn-ghost" data-testid="hero-services-cta">
                  Nos prestations
                </Link>
              </div>
            </div>

            <div className="md:col-span-4 fade-up fade-up-2">
              <div className="border-l-2 border-[#ffd600] pl-5">
                <div className="font-mono-tech text-xs uppercase tracking-widest text-neutral-500 mb-2">/ Engagement chantier</div>
                <div className="font-display text-2xl font-semibold leading-tight">
                  Qualité, sécurité, maîtrise technique et fiabilité — sur chaque mètre cube coulé.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* bottom stats strip */}
        <div className="absolute bottom-0 left-0 right-0 z-10 border-t border-white/10 bg-black/60 backdrop-blur-md">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 grid grid-cols-2 md:grid-cols-4 gap-6">
            {STATS.map((s) => (
              <div key={s.label} data-testid={`hero-stat-${s.label}`}>
                <div className="font-display text-3xl md:text-4xl text-[#ffd600] font-semibold leading-none">{s.value}</div>
                <div className="font-mono-tech text-[10px] md:text-xs uppercase tracking-widest text-neutral-400 mt-2">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <div className="eyebrow mb-4">/ 01 — Prestations</div>
              <h2 className="font-display font-bold uppercase tracking-tight text-4xl md:text-6xl leading-[0.95]">
                Le gros œuvre,<br /><span className="text-[#ffd600]">de A à Z.</span>
              </h2>
            </div>
            <p className="text-neutral-400 max-w-md text-lg leading-relaxed">
              Sept spécialités intégrées qui font la solidité d’un bâtiment neuf. Nos équipes pilotent chaque étape, du coffrage à la dalle de toiture — sans oublier la remise en état finale.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
            {SERVICES.map((svc, i) => (
              <Link
                key={svc.slug}
                to={`/services#${svc.slug}`}
                data-testid={`home-service-${svc.slug}`}
                className={`brut-card group ${i === 0 ? "md:col-span-7" : i === 1 ? "md:col-span-5" : i === 2 ? "md:col-span-5" : i === 3 ? "md:col-span-4" : "md:col-span-3"} min-h-[260px] flex flex-col justify-between`}
              >
                <div className="flex items-start justify-between">
                  <i className={`${svc.icon} text-[#ffd600] text-2xl`} />
                  <span className="font-mono-tech text-[10px] uppercase tracking-widest text-neutral-500">0{i + 1}</span>
                </div>
                <div className="mt-8">
                  <div className="font-display text-2xl font-semibold leading-tight uppercase">{svc.title}</div>
                  <p className="text-neutral-400 mt-3 leading-relaxed">{svc.short}</p>
                  <div className="mt-5 flex items-center gap-2 text-[#ffd600] font-display uppercase tracking-widest text-xs opacity-80 group-hover:opacity-100 transition-opacity">
                    En savoir plus <i className="fa-solid fa-arrow-right" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES + IMAGE */}
      <section className="relative py-24 md:py-32 overflow-hidden border-t border-white/5">
        <div className="absolute inset-0 diag-lines opacity-50" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6 order-2 md:order-1">
            <div className="relative">
              <img src={REBAR_IMG} alt="Béton armé" className="w-full h-[460px] object-cover" />
              <div className="absolute -bottom-6 -right-6 hazard-stripes h-16 w-32 hidden md:block" />
              <div className="absolute top-6 left-6 bg-black/80 border border-[#ffd600]/30 px-4 py-2 font-mono-tech text-xs tracking-widest uppercase">
                / Béton armé · Conforme Eurocodes
              </div>
            </div>
          </div>
          <div className="md:col-span-6 order-1 md:order-2">
            <div className="eyebrow mb-4">/ 02 — Engagements</div>
            <h2 className="font-display font-bold uppercase tracking-tight text-4xl md:text-5xl leading-[0.95]">
              Quatre piliers.<br />
              <span className="text-[#ffd600]">Zéro compromis.</span>
            </h2>
            <p className="text-neutral-400 mt-5 leading-relaxed max-w-lg">
              Un chantier DCR-BATI, c’est une organisation rigoureuse, des compagnons formés et un matériel calibré. Nous garantissons la solidité de chaque ouvrage et la sérénité de chaque maître d’ouvrage.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-10">
              {VALUES.map((v) => (
                <div key={v.title} className="brut-card !p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <i className={`${v.icon} text-[#ffd600] text-xl`} />
                    <div className="font-display uppercase font-semibold">{v.title}</div>
                  </div>
                  <div className="text-sm text-neutral-400">{v.text}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROJECT TEASER */}
      <section className="relative py-24 md:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <div className="eyebrow mb-4">/ 03 — Réalisations</div>
              <h2 className="font-display font-bold uppercase tracking-tight text-4xl md:text-6xl leading-[0.95]">
                Sélection<br /><span className="text-[#ffd600]">chantiers récents.</span>
              </h2>
            </div>
            <Link to="/realisations" className="btn-ghost" data-testid="home-realisations-cta">
              Voir toutes les réalisations <i className="fa-solid fa-arrow-right" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {PROJECTS.slice(0, 3).map((p) => (
              <article key={p.name} className="group relative overflow-hidden border border-[#262626] bg-[#141414]">
                <div className="relative h-64 overflow-hidden">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 grayscale group-hover:grayscale-0" />
                  <div className="absolute top-3 left-3 bg-[#ffd600] text-black text-[10px] font-display uppercase tracking-widest px-2 py-1">{p.tag}</div>
                </div>
                <div className="p-5">
                  <div className="font-mono-tech text-[10px] uppercase tracking-widest text-neutral-500">{p.location} · {p.type}</div>
                  <div className="font-display text-xl font-semibold mt-2 uppercase">{p.name}</div>
                  <div className="text-sm text-neutral-400 mt-1">Surface · <span className="text-[#ffd600]">{p.surface}</span></div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 md:py-28 border-t border-white/5 overflow-hidden">
        <div className="hazard-stripes absolute top-0 left-0 right-0 h-2" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <div className="eyebrow mb-3">/ Lancer un projet</div>
            <h2 className="font-display font-bold uppercase tracking-tight text-4xl md:text-6xl leading-[0.95]">
              Un chantier neuf ?<br />
              <span className="text-[#ffd600]">Parlons gros œuvre.</span>
            </h2>
            <p className="text-neutral-400 mt-4 max-w-2xl leading-relaxed">
              Décrivez‑nous votre projet : nous étudions le dossier, planifions les phases techniques et vous remettons un devis détaillé sous 5 jours ouvrés.
            </p>
          </div>
          <div className="md:col-span-4 flex flex-wrap gap-4 justify-start md:justify-end">
            <Link to="/devis" className="btn-primary" data-testid="footer-devis-cta">
              Demander un devis <i className="fa-solid fa-arrow-right" />
            </Link>
            <Link to="/contact" className="btn-ghost" data-testid="footer-contact-cta">
              Nous contacter
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
